/**
 * iCMUWall Main Application Component
 */

import '../../../ui-styles/ibest-style.css';
import { useMemo, type ReactNode } from 'react';
import { DIF_TYPES, SUPPORT_TYPES, defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { APIResponse } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, RESPONSE_DISPLAY_CONFIG } from './appConfig';

import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, ResponseDisplay, GenerateLinkButton } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  // Merge URL parameters with default values
  const initialValues = useMemo<FormValues>(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'iCMUWall',
          length: '12',
          tributaryWidth: '16/12',
          addedWeight: '0',
          supports: 1,
          rebar: '6',
          rebarDepth: '(8-3/8)/2',
          shearRebar: '0-n4@16',
          density: '115',
          dif: '0',
          b: '16',
          h: '8-3/8',
          fy: '1.1*60000',
          fc: '1500',
          axialLoad: '0',
          pressureHistory: '4, 0,0',
          timeHistory: '0, 14, 30',
          u0: '0.0',
          v0: '0.0'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  // Initialize form with react-hook-form + Zod
  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({
    schema, // Type assertion to handle coercion differences
    defaultValues: initialValues
  });

  // Watch form values for dependent logic
  const values = watch();

  const { saveDataToDB } = useAppStorage<FormValues, APIResponse>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, APIResponse>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, APIResponse>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  /* prettier-ignore */
  const fields = defineFields<FormValues>([
	{kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
	{kind: 'input', name: 'length', label: 'Length', unit: 'ft', helpText: 'Enter the length of the component', placeholder: 'Component Length' },
	{kind: 'input', name: 'tributaryWidth', label: 'Tributary Width', unit: 'ft', helpText: 'Enter the tributary width of the component', placeholder: 'Component Tributary Width'},
	{kind: 'input', name: 'addedWeight', label: 'Added Weight', unit: 'psf', helpText: 'Enter the added weight on the component', placeholder: 'Component Added Weight'},
	{kind: 'dropdown', name: 'supports', label: 'Support', options: SUPPORT_TYPES, helpText: 'Enter the support type...'},
	{kind: 'input', name: 'b', label: 'CMU b', unit: 'in', helpText: 'Enter the width of concrete beam/column', placeholder: 'Width b' },
	{kind: 'input', name: 'h', label: 'CMU h', unit: 'in', helpText: 'Enter the height of concrete beam/column', placeholder: 'Height h' },
	{ kind: 'input', name: 'rebar', label: 'Rebar #', unit: '', helpText: 'Rebar Number', placeholder: 'Rebar #' },
	{ kind: 'input', name: 'rebarDepth', label: 'Rebar Depth', unit: 'in', helpText: 'Rebar Depth', placeholder: 'Rebar Depth' },
	{ kind: 'input', name: 'shearRebar', label: 'Shear Rebar', unit: '', helpText: 'Shear Rebar (e.g., 2-n4@4)', placeholder: 'Shear Rebar' },
	{kind: 'input', name: 'fy', label: (<span>F<sub>y</sub></span>), unit: 'psi', helpText: 'Dynamic yield strength Fdy', placeholder: 'Dynamic yield strength Fdy'},
	{kind: 'input', name: 'fc', label: (<span>F<sub>c</sub></span>), unit: 'psi', helpText: 'Concrete Compressive Strength',placeholder: 'Concrete Compressive Strength'},
	{kind: 'input', name: 'density', label: 'Density', unit: 'pcf', helpText: 'Density of CMU', placeholder: 'Density of CMU' },
	{kind: 'dropdown', name: 'dif', label: 'DIFs', options: DIF_TYPES , helpText: 'DIF for Concrete'},
	{ kind: 'input', name: 'axialLoad', label: 'Axial Load', unit: 'kips', helpText: 'Axial Load', placeholder: 'Axial Load' },
	{ kind: 'input', name: 'pressureHistory', label: 'Pressure History', unit: 'psi', helpText: 'Pressure History', placeholder: 'Pressure History' },
	{ kind: 'input', name: 'timeHistory', label: 'Time History', unit: 'msec', helpText: 'Time History', placeholder: 'Time History' },
	{ kind: 'input', name: 'u0', label: 'Initial Displacement', unit: 'in', helpText: 'Initial Displacement', placeholder: 'Initial Displacement' },
	{ kind: 'input', name: 'v0', label: 'Initial Velocity', unit: 'in/sec', helpText: 'Initial Velocity', placeholder: 'Initial Velocity' }
	]);

  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">CMU Wall Analysis</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button mx-2" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid grid-rows-[auto_1fr] rounded-xl gap-y-0 w-fit">
          <div className="analysis-app__results shadow-div rounded-xl m-1">
            {/* The Steel Element Type response goes here  */}
            <ResponseDisplay data={analysisResult} sections={RESPONSE_DISPLAY_CONFIG} emptyMessage="No analysis results yet." />
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.charts && (
              <div className="analysis-app__charts-grid grid grid-cols-2 gap-2 m-5 print:gap-1">
                {Object.entries(analysisResult.charts)
                  .filter(([, v]) => !!v)
                  .map(([key, v]) => {
                    const chart = v!;
                    const src = `data:${chart.mime};base64,${chart.base64}`;

                    return (
                      <div key={key} className="analysis-app__chart-card break-inside-avoid">
                        <img src={src} alt={`${key} chart`} loading="lazy" className="w-full h-auto object-contain rounded-none max-w-250" />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div className="hidden print:block print-root ">
          <div className="print-a4-landscape border border-black box-border bg-white">
            <div className="hidden print:grid h-full min-h-0 grid-cols-[24%_76%] print:bg-white print:text-black">
              <div className="flex h-full min-h-0 flex-col">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iSteel Element'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>

                  <div className="m-0 p-0 ">
                    <table className="w-full table-fixed text-[10px] m-0 p-0">
                      <colgroup>
                        <col className="w-[55%]" />
                        <col className="w-[45%]" />
                      </colgroup>

                      <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Inputs</caption>

                      <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none [&>tr>td:nth-child(2)]:break-all">
                        {(
                          [
                            ['Length (ft)', values.length],
                            ['Tributary Width (ft)', values.tributaryWidth],
                            ['Added Weight (psf)', values.addedWeight],
                            ['Support', SUPPORT_TYPES.find(option => option.value == values.supports)?.label || '—'],
                            ['b (in)', values.b],
                            ['h (in)', values.h],
                            ['Rebar #', values.rebar],
                            ['Rebar Depth (in)', values.rebarDepth],
                            ['Shear Rebar', values.shearRebar],
                            ['Fy (psi)', values.fy],
                            ['Fc (psi)', values.fc],
                            ['DIF', DIF_TYPES.find(option => option.value == values.dif)?.label || '—'],
                            ['Axial Load (kips)', values.axialLoad],
                            ['u0', `${values.u0} in`],
                            ['v0', `${values.v0} in/msec`]
                          ] as Array<[ReactNode, ReactNode]>
                        ).map(([label, value], i) => (
                          <tr key={i}>
                            <td className="font-semibold">{label}</td>
                            <td>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    {analysisResult && (
                      <table className="w-full table-fixed text-[10px] m-0 p-0">
                        <colgroup>
                          <col className="w-[55%]" />
                          <col className="w-[45%]" />
                        </colgroup>

                        <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Results</caption>

                        <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none">
                          {[
                            [<>Effective Mass</>, `${analysisResult.Me} (lbs·sec²/in)`],

                            ['Max Disp ', `${analysisResult.Xmax} in @ ${analysisResult.tmax} msec`],
                            ['Min Disp ', `${analysisResult.Xmin} in @ ${analysisResult.tmin} msec`],
                            ['Rebar Weight ', analysisResult.rebar_weight],
                            ['CMU Volume ', `${analysisResult.cmu_volume} (cu yard)`],
                            ['GWP ', `${analysisResult.GWP} (kg CO2e)`],

                            ['Below Values are for Inbound and Rebound', ''],

                            ['Time Period ', analysisResult.Tn ? `${analysisResult.Tn[0]} / ${analysisResult.Tn[1]} (msec)` : '—'],
                            ['Resistance ', analysisResult.Ru ? `${analysisResult.Ru[0].toFixed(3)} / ${analysisResult.Ru[1].toFixed(3)} (kips)` : '—'],
                            ['Ductility ', analysisResult.duct ? `${analysisResult.duct[0]} / ${analysisResult.duct[1]}` : '—'],
                            ['Rotation ', analysisResult.rot ? `${analysisResult.rot[0]} / ${analysisResult.rot[1]} (deg)` : '—'],
                            ['Support 1 ', analysisResult.V1 ? `${analysisResult.V1[0]} / ${analysisResult.V1[1]} (kips)` : '—'],
                            ['Support 2 ', analysisResult.V2 ? `${analysisResult.V2[0]} / ${analysisResult.V2[1]} (kips)` : '—']
                          ].map(([label, value], i) => (
                            <tr key={i}>
                              <td className="font-semibold">{label}</td>
                              <td>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Shareable Link at Bottom */}
                <div className="mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    🔗 LINK
                  </a>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 pl-2">
                  {analysisResult?.charts && (
                    <div className="grid grid-cols-2 gap-2 print:gap-1 m-2 h-full">
                      {Object.entries(analysisResult.charts)
                        .filter(([, v]) => !!v)
                        .map(([key, v]) => {
                          const chart = v!;
                          const src = `data:${chart.mime};base64,${chart.base64}`;

                          return (
                            <div key={key} className="break-inside-avoid">
                              <img src={src} alt={`${key} chart`} loading="lazy" className="w-full h-auto object-contain rounded-none max-w-250" />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
